/**
 * Tenant Configuration
 *
 * Provides runtime configuration for multi-tenant AWS services.
 * All configuration comes from environment variables set by deployment pipeline.
 *
 * NO DEFAULTS - Fails fast with clear errors if required variables missing.
 */

/**
 * Require environment variable (throw if missing)
 * @param {string} varName - Environment variable name
 * @returns {string} Variable value
 * @throws {Error} If variable not set
 */
const requireEnv = (varName) => {
  const value = import.meta.env[varName];
  if (!value) {
    throw new Error(`❌ ERROR: Required environment variable ${varName} is not set. Run bin/frontend/configure-api-endpoints.sh to configure.`);
  }
  return value;
};

/**
 * Optional environment variable (returns undefined if missing)
 * @param {string} varName - Environment variable name
 * @returns {string|undefined} Variable value or undefined
 */
const optionalEnv = (varName) => {
  return import.meta.env[varName];
};

/**
 * Get full resource prefix: {PROJECT}-{RESOURCE_PREFIX}-{STAGE}
 * Example: KW-ENV33-QA
 */
const getResourcePrefix = () => {
  // TABLE_PREFIX from deployment already includes PROJECT-RESOURCE_PREFIX
  // Example: KW-ENV33
  const tablePrefix = requireEnv('VITE_TABLE_PREFIX');
  const environment = requireEnv('VITE_ENVIRONMENT');

  return `${tablePrefix}-${environment}`;
};

/**
 * Get AWS region
 */
const getRegion = () => {
  return optionalEnv('VITE_AWS_REGION') ||
         optionalEnv('VITE_AWS_COGNITO_REGION') ||
         'eu-west-2'; // Default region
};

/**
 * Get tenant configuration
 * @returns {Object} Tenant configuration object
 */
export const getTenantConfig = () => {
  const region = getRegion();
  const prefix = getResourcePrefix();

  return {
    // AWS Region
    region,

    // Resource Prefix (e.g., KW-ENV33-QA)
    prefix,

    // Cognito Configuration
    cognito: {
      region,
      userPoolId: requireEnv('VITE_AWS_USER_POOLS_ID'),
      clientId: requireEnv('VITE_AWS_USER_POOLS_WEB_CLIENT_ID'),
    },

    // API Configuration
    api: {
      // Face Match API (from CloudFormation FaceMatch stack)
      faceMatch: requireEnv('VITE_FACE_MATCH_API'),

      // User Events API (from CloudFormation UserEventsAPI stack)
      usereventapi: optionalEnv('VITE_USER_EVENTS_API'),

      // User Events Aggregation API (from CloudFormation EventAggregation stack)
      usereventsaggregation: optionalEnv('VITE_AGGREGATION_API'),

      // Detect Face Attributes API (from CloudFormation DetectFaceAttributesAPI stack)
      detectFaceAttributes: optionalEnv('VITE_DETECT_FACE_ATTRIBUTES_API'),

      // Get Persons API (from CloudFormation GetPersonsAPI stack)
      getPersons: optionalEnv('VITE_GET_PERSONS_API'),

      // Query Persons API (from CloudFormation QueryPersonsAPI stack)
      queryPersons: optionalEnv('VITE_QUERY_PERSONS_API'),

      // Person Update API (from CloudFormation PersonUpdateAPI stack)
      personUpdate: optionalEnv('VITE_PERSON_UPDATE_API'),

      // Get Current Time API (from CloudFormation GetCurrentTimeAPI stack)
      getCurrentTime: optionalEnv('VITE_GET_CURRENT_TIME_API'),

      // User Management API (from CloudFormation UserManagementAPI stack)
      useradminapi: optionalEnv('VITE_USER_MANAGEMENT_API'),

      // Tenant Configuration API (from CloudFormation TenantConfigAPI stack)
      tenantconfigapi: optionalEnv('VITE_TENANT_CONFIG_API'),

      // Dataset Management API (from CloudFormation DatasetManagementAPI stack)
      datasetManagement: optionalEnv('VITE_DATASET_MANAGEMENT_API'),

      // Collection Rebuild API (from CloudFormation CollectionRebuildAPI stack)
      collectionRebuild: optionalEnv('VITE_COLLECTION_REBUILD_API'),

      // Person Management API (from CloudFormation PersonManagementAPI stack)
      personManagement: optionalEnv('VITE_PERSON_MANAGEMENT_API'),

      // Unknown Faces API (from CloudFormation UnknownFacesAPI-Gateway stack)
      unknownFaces: optionalEnv('VITE_UNKNOWN_FACES_API'),

      // Sender Registry API (from CloudFormation SenderRegistryAPI-Gateway stack)
      senderRegistry: optionalEnv('VITE_SENDER_REGISTRY_API'),

      // Auth API (from CloudFormation AuthAPI stack)
      auth: optionalEnv('VITE_AUTH_API'),

      // Subscription API (from CloudFormation SubscriptionAPI-Gateway stack)
      subscription: optionalEnv('VITE_SUBSCRIPTION_API'),

      // Witness Statement API (from CloudFormation StatementInfrastructure stack)
      witnessStatement: optionalEnv('VITE_WITNESS_STATEMENT_API'),
    },

    // S3 Configuration
    s3: {
      region,
      // Bucket will be constructed per-tenant at runtime
      // Format: {project}-{resource}-{stage}-{tenant}-captures (lowercase)
      // Example: kw-env33-qa-met-captures
      // Actual bucket name determined by getTenantBucket() below
    },

    // GraphQL Configuration (if using AppSync)
    graphql: {
      endpoint: optionalEnv('VITE_AWS_APPSYNC_GRAPHQL_ENDPOINT'),
      region: optionalEnv('VITE_AWS_APPSYNC_REGION') || region,
      apiKey: optionalEnv('VITE_AWS_APPSYNC_API_KEY'),
      defaultAuthMode: 'API_KEY',
    },

    // WebSocket Configuration (optional)
    websocket: {
      url: optionalEnv('VITE_WEBSOCKET_URL'),
    },
  };
};

/**
 * Get current tenant ID from user attributes (after authentication)
 * This is set in the JWT token as custom:tenantId
 * @param {Object} user - Cognito user object (optional - will try to get from localStorage token if not provided)
 * @returns {string|null} Tenant ID or null if not found
 */
export const getTenantId = (user) => {
  // If user object provided, extract from attributes
  if (user && user.attributes) {
    return user.attributes['custom:tenantId'] || null;
  }

  // Otherwise try to decode from stored JWT token
  try {
    const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${requireEnv('VITE_AWS_USER_POOLS_WEB_CLIENT_ID')}.LastAuthUser`);
    if (!idToken) return null;

    const tokenKey = `CognitoIdentityServiceProvider.${requireEnv('VITE_AWS_USER_POOLS_WEB_CLIENT_ID')}.${idToken}.idToken`;
    const token = localStorage.getItem(tokenKey);
    if (!token) return null;

    // Decode JWT payload (middle part between dots)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload['custom:tenantId'] || null;
  } catch (error) {
    console.warn('[getTenantId] Failed to extract tenant ID from token:', error);
    return null;
  }
};

/**
 * Get tenant-specific S3 bucket name
 * @param {string} tenantId - Tenant ID (e.g., MET, MI5)
 * @returns {string} S3 bucket name
 */
export const getTenantBucket = (tenantId) => {
  if (!tenantId) {
    throw new Error('❌ ERROR: Tenant ID required to construct bucket name');
  }

  const tablePrefix = requireEnv('VITE_TABLE_PREFIX');
  const environment = requireEnv('VITE_ENVIRONMENT');

  // Convert to lowercase for S3 bucket naming rules
  // Format: {project}-{resource}-{stage}-{tenant}-captures
  // Example: kw-env33-qa-met-captures
  return `${tablePrefix}-${environment}-${tenantId}-captures`.toLowerCase();
};

/**
 * Get tenant-specific reference bucket name
 * @param {string} tenantId - Tenant ID (e.g., MET, MI5)
 * @returns {string} S3 reference bucket name
 */
export const getTenantReferenceBucket = (tenantId) => {
  if (!tenantId) {
    throw new Error('❌ ERROR: Tenant ID required to construct reference bucket name');
  }

  const tablePrefix = requireEnv('VITE_TABLE_PREFIX');
  const environment = requireEnv('VITE_ENVIRONMENT');

  // Convert to lowercase for S3 bucket naming rules
  // Format: {project}-{resource}-{stage}-{tenant}-reference
  // Example: kw-env33-qa-met-reference
  return `${tablePrefix}-${environment}-${tenantId}-reference`.toLowerCase();
};

/**
 * Get person image URL from S3 reference bucket
 * @param {string} imageKey - S3 key (e.g., "public/faces/LORDS-LORD10115.jpg")
 * @param {string} tenantId - Tenant ID
 * @returns {string|null} Full S3 URL or null if no image key
 */
export const getPersonImageUrl = (imageKey, tenantId) => {
  if (!imageKey || !tenantId) {
    return null;
  }

  const referenceBucket = getTenantReferenceBucket(tenantId);
  const region = getRegion();

  // Construct S3 URL: https://{bucket}.s3.{region}.amazonaws.com/{key}
  return `https://${referenceBucket}.s3.${region}.amazonaws.com/${imageKey}`;
};

/**
 * Get S3 key with tenant prefix
 * @param {string} key - Base S3 key
 * @param {string} tenantId - Tenant ID
 * @returns {string} Full S3 key with tenant prefix
 */
export const getTenantS3Key = (key, tenantId) => {
  if (!tenantId) {
    throw new Error('❌ ERROR: Tenant ID required for S3 key prefix');
  }

  // Don't add prefix if already prefixed
  if (key.startsWith(`${tenantId}/`)) {
    return key;
  }

  return `${tenantId}/${key}`;
};

/**
 * Get API endpoint for specific API name
 * @param {string} apiName - API name (e.g., 'faceMatch', 'userEvents')
 * @returns {string|null} API endpoint URL or null if not configured
 */
export const getApiEndpoint = (apiName) => {
  const config = getTenantConfig();

  if (config.api[apiName]) {
    return config.api[apiName];
  }

  // Return null for optional APIs instead of throwing
  // This allows features to gracefully disable when API not available
  console.warn(`⚠️  API endpoint '${apiName}' not configured - feature will be disabled`);
  return null;
};

/**
 * Get Auth API URL
 * @returns {string} Auth API base URL
 * @throws {Error} If Auth API URL not configured
 */
export const getAuthApiUrl = () => {
  const authApiUrl = optionalEnv('VITE_AUTH_API');

  if (!authApiUrl) {
    throw new Error('❌ ERROR: Auth API URL (VITE_AUTH_API) not configured. Run bin/frontend/configure-api-endpoints.sh');
  }

  return authApiUrl;
};

/**
 * Validate configuration on app startup
 * Throws if required config missing
 */
export const validateConfig = () => {
  try {
    const config = getTenantConfig();

    console.log('✓ Configuration validated:', {
      region: config.region,
      prefix: config.prefix,
      cognito: {
        userPoolId: config.cognito.userPoolId,
        clientId: config.cognito.clientId ? '***' : 'MISSING',
      },
      api: {
        faceMatch: config.api.faceMatch ? 'configured' : 'MISSING',
      },
    });

    return true;
  } catch (error) {
    console.error('❌ Configuration validation failed:', error.message);
    throw error;
  }
};

// Export for backwards compatibility
export default {
  getTenantConfig,
  getTenantId,
  getTenantBucket,
  getTenantReferenceBucket,
  getPersonImageUrl,
  getTenantS3Key,
  getApiEndpoint,
  validateConfig,
};
